import { useExperimentChartOverlays } from 'features/timeSeries/experiment/ExperimentLabContext'
import {
  buildSentinelBandOverlays,
  SENTINEL_OUT_OF_BAND_COLOR,
  SentinelBandOverlay,
  useSentinelScores,
} from 'features/timeSeries/hooks/useSentinelScores'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { useMemo } from 'react'

/**
 * Live sentinel overlays keyed by target sensor id, for surfaces that
 * annotate a pen with its sentinel's prediction (tooltip, legends). Reads
 * the same SWR score cache as the chart overlay, so consumers never trigger
 * extra scoring requests. Hidden sentinels are excluded unless
 * `includeHidden` — management surfaces (the pen drawer) pass it so a hidden
 * sentinel's row stays available to toggle back on.
 *
 * The Experiment Lab's trained band shares the overlay shape, so the same
 * map also carries the experiment's prediction for its target pen (only when
 * no sentinel already covers that pen — sentinel readouts win a conflict,
 * since their rows are tied to sentinel management UI).
 */
export const useSentinelOverlayMap = ({
  includeHidden = false,
}: { includeHidden?: boolean } = {}): Map<string, SentinelBandOverlay> => {
  const {
    siteID,
    timeRange,
    dataLayers,
    activeSentinels,
    sentinelVisibilityConfig,
  } = useScatterplotContext()

  const sentinelsEnabled =
    dataLayers.find((layer) => layer.id === 'sentinels')?.enabled ?? false
  const { scores } = useSentinelScores(siteID, activeSentinels, timeRange, {
    enabled: sentinelsEnabled,
  })
  const { bandOverlay: experimentBandOverlay } = useExperimentChartOverlays()

  return useMemo(() => {
    const visibleSentinels = includeHidden
      ? activeSentinels
      : activeSentinels.filter(
          (sentinel) => sentinelVisibilityConfig[sentinel.entryId] !== false
        )
    const overlays = buildSentinelBandOverlays({
      activeSentinels: visibleSentinels,
      scores,
      allSensorsToRender: [],
    })
    const map = new Map(
      overlays.map((overlay) => [overlay.targetSensorId, overlay])
    )
    if (
      experimentBandOverlay &&
      !map.has(experimentBandOverlay.targetSensorId)
    ) {
      map.set(experimentBandOverlay.targetSensorId, {
        ...experimentBandOverlay,
        color: SENTINEL_OUT_OF_BAND_COLOR,
      })
    }
    return map
  }, [
    activeSentinels,
    scores,
    sentinelVisibilityConfig,
    includeHidden,
    experimentBandOverlay,
  ])
}

/** True while sentinel scoring for this chart is in flight. */
export const useSentinelScoringActive = (): boolean => {
  const { siteID, timeRange, dataLayers, activeSentinels } =
    useScatterplotContext()

  const sentinelsEnabled =
    dataLayers.find((layer) => layer.id === 'sentinels')?.enabled ?? false
  const { isLoading } = useSentinelScores(siteID, activeSentinels, timeRange, {
    enabled: sentinelsEnabled,
  })
  return sentinelsEnabled && activeSentinels.length > 0 && isLoading
}
